// =============================================================================
// Hosted-agent working state (#3023 — HERMES)
//
// The human half of the proposal loop, behind
// `require_admin_capability("hosted_agents")`. Agents can only PROPOSE
// working-state values (the `agent_working_state` tool); here an admin
// reviews the queue:
//   * confirm — proposal becomes the active value (refused 409 on a pinned fact)
//   * reject  — proposal is discarded
//   * correct (PUT) — a direct human value; also PINS the fact so no agent
//     proposal can ever replace it (the issue's non-negotiable invariant)
//   * create  — an admin-made fact, born active + pinned
//
// Backend: routes/admin/hosted_agent_working_state.py,
// services/hosted_agents/working_state.py.
// =============================================================================

import axios from "axios"

import { api } from "@/lib/api-client"

export type WorkingStateValueType = "number" | "string" | "boolean" | "json"

export const WORKING_STATE_VALUE_TYPES: readonly WorkingStateValueType[] = [
  "number",
  "string",
  "boolean",
  "json",
] as const

// Caps mirrored from the backend (services/hosted_agents/working_state.py) so
// the panel can refuse before the round trip.
export const MAX_WORKING_STATE_FACTS_PER_AGENT = 200
export const MAX_WORKING_STATE_PENDING_PROPOSALS = 5
export const MAX_WORKING_STATE_KEY_LENGTH = 120
export const MAX_WORKING_STATE_SOURCE_LENGTH = 500
export const MAX_WORKING_STATE_LABEL_LENGTH = 200

/** Who established a value or proposal: an agent run, or a human. */
export type WorkingStateProvenance = {
  actor: "agent" | "user"
  user_id?: string | null
  run_id?: string | null
  session_id?: string | null
  initiator?: string | null
  recorded_at: string
}

/** One typed value with its source and validity — active or historical. */
export type WorkingStateValue = {
  value: unknown
  value_type: WorkingStateValueType
  unit?: string | null
  source: string
  valid_until?: string | null
  note?: string | null
  provenance: WorkingStateProvenance
}

/** An agent-suggested value awaiting confirmation. */
export type WorkingStateProposal = {
  proposal_id: string
  value: unknown
  value_type: WorkingStateValueType
  unit?: string | null
  source: string
  valid_until?: string | null
  note?: string | null
  provenance: WorkingStateProvenance
}

/** One (agent, key) fact in the ADMIN shape: proposals and history included. */
export type WorkingStateFact = {
  /** `fact:<agent-uuid>:<key>` */
  id: string
  agent_id: string
  key: string
  label?: string | null
  active: WorkingStateValue | null
  /** True after a human correction — agent proposals can never replace the value. */
  pinned: boolean
  proposals: WorkingStateProposal[]
  history: WorkingStateValue[]
  pending_count: number
  created_at: string
  updated_at: string
}

/** Payload for PUT (correct) and POST (create — which also needs `key`). */
export type WorkingStateWriteInput = {
  key?: string
  value: unknown
  value_type: WorkingStateValueType
  unit?: string | null
  source: string
  valid_until?: string | null
  note?: string | null
  label?: string | null
}

export type WorkingStateErrorCode =
  | "working_state_not_found"
  | "working_state_invalid"
  | "working_state_conflict"
  | "working_state_unavailable"
  | "hosted_agent_not_found"

export class HostedAgentWorkingStateError extends Error {
  code: WorkingStateErrorCode
  status: number | undefined

  constructor(input: { code: WorkingStateErrorCode; message: string; status?: number }) {
    super(input.message)
    this.name = "HostedAgentWorkingStateError"
    this.code = input.code
    this.status = input.status
  }
}

const SERVER_ERROR_CODES = new Set<string>([
  "working_state_not_found",
  "working_state_invalid",
  "working_state_conflict",
  "hosted_agent_not_found",
])

function rethrowWorkingStateError(error: unknown): never {
  if (!axios.isAxiosError(error)) throw error
  const status = error.response?.status
  const data = (error.response?.data ?? {}) as { error?: string; code?: string }
  const message = typeof data.error === "string" && data.error ? data.error : error.message
  const code = typeof data.code === "string" ? data.code : undefined
  if (code && SERVER_ERROR_CODES.has(code)) {
    throw new HostedAgentWorkingStateError({
      code: code as WorkingStateErrorCode,
      message,
      status,
    })
  }
  if (status === 503) {
    throw new HostedAgentWorkingStateError({ code: "working_state_unavailable", message, status })
  }
  if (status === 404) {
    throw new HostedAgentWorkingStateError({ code: "hosted_agent_not_found", message, status })
  }
  throw error
}

function workingStateBase(agentRef: string): string {
  return `/admin/hosted-agents/${encodeURIComponent(agentRef)}/working-state`
}

/** Every fact for one agent — active value, pending proposals, history, pinned. */
export async function listWorkingState(agentRef: string): Promise<WorkingStateFact[]> {
  try {
    const response = await api.get(workingStateBase(agentRef))
    const data = response.data || response
    return Array.isArray(data?.facts) ? (data.facts as WorkingStateFact[]) : []
  } catch (error) {
    rethrowWorkingStateError(error)
  }
}

/** Accept an agent proposal as the active value (409 on a pinned fact). */
export async function confirmWorkingStateProposal(
  agentRef: string,
  factId: string,
  proposalId: string
): Promise<WorkingStateFact> {
  try {
    const response = await api.post(
      `${workingStateBase(agentRef)}/${encodeURIComponent(factId)}/proposals/${encodeURIComponent(proposalId)}/confirm`
    )
    return (response.data || response).fact as WorkingStateFact
  } catch (error) {
    rethrowWorkingStateError(error)
  }
}

/** Discard an agent proposal without touching the active value. */
export async function rejectWorkingStateProposal(
  agentRef: string,
  factId: string,
  proposalId: string
): Promise<WorkingStateFact> {
  try {
    const response = await api.post(
      `${workingStateBase(agentRef)}/${encodeURIComponent(factId)}/proposals/${encodeURIComponent(proposalId)}/reject`
    )
    return (response.data || response).fact as WorkingStateFact
  } catch (error) {
    rethrowWorkingStateError(error)
  }
}

/** Human correction: sets the active value AND pins the fact. */
export async function correctWorkingStateFact(
  agentRef: string,
  factId: string,
  input: WorkingStateWriteInput
): Promise<WorkingStateFact> {
  try {
    const response = await api.put(
      `${workingStateBase(agentRef)}/${encodeURIComponent(factId)}`,
      input
    )
    return (response.data || response).fact as WorkingStateFact
  } catch (error) {
    rethrowWorkingStateError(error)
  }
}

/** Admin-created fact: born active + pinned with user provenance. */
export async function createWorkingStateFact(
  agentRef: string,
  input: WorkingStateWriteInput
): Promise<WorkingStateFact> {
  try {
    const response = await api.post(workingStateBase(agentRef), input)
    return (response.data || response).fact as WorkingStateFact
  } catch (error) {
    rethrowWorkingStateError(error)
  }
}

/** Lift the human pin so a later confirmation may replace the value. */
export async function unpinWorkingStateFact(
  agentRef: string,
  factId: string
): Promise<WorkingStateFact> {
  try {
    const response = await api.post(
      `${workingStateBase(agentRef)}/${encodeURIComponent(factId)}/unpin`
    )
    return (response.data || response).fact as WorkingStateFact
  } catch (error) {
    rethrowWorkingStateError(error)
  }
}

/** Remove one fact permanently (active value, proposals and history). */
export async function deleteWorkingStateFact(
  agentRef: string,
  factId: string
): Promise<WorkingStateFact> {
  try {
    const response = await api.delete(`${workingStateBase(agentRef)}/${encodeURIComponent(factId)}`)
    return (response.data || response).fact as WorkingStateFact
  } catch (error) {
    rethrowWorkingStateError(error)
  }
}
